import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

export type ModalParams = {
  component: React.ComponentType<any>;
  props?: Record<string, any>;
  size?: string;
  title?: string;
  onDismiss?: () => void;
};

let openModalFn: ((params: ModalParams) => void) | null = null;
let updateModalFn: ((params: Partial<ModalParams>) => void) | null = null;
let closeModalFn: (() => void) | null = null;
let isModalOpen = false;

export const setModalHandler = (
  openHandler: (params: ModalParams) => void,
  updateHandler: (params: Partial<ModalParams>) => void,
  closeHandler: () => void,
) => {
  openModalFn = openHandler;
  updateModalFn = updateHandler;
  closeModalFn = closeHandler;
};

export const openModal = (params: ModalParams) => {
  if (openModalFn) {
    openModalFn(params);
    isModalOpen = true;
  }
};

export const updateModal = (params: Partial<ModalParams>) => {
  if (updateModalFn) updateModalFn(params);
};

export const closeModal = () => {
  if (closeModalFn) {
    closeModalFn();
    isModalOpen = false;
  }
};

export const getIsModalOpen = () => isModalOpen;

const ModalService: React.FC = () => {
  const [modalContent, setModalContent] = useState<ModalParams | null>(null);

  const handleOpenModal = (params: ModalParams) => {
    setModalContent(params);
  };

  const handleUpdateModal = (params: Partial<ModalParams>) => {
    setModalContent((prev) => ({
      ...prev,
      ...params,
      props: { ...prev?.props, ...params.props },
    }));
  };

  const handleCloseModal = () => {
    setModalContent(null);
  };

  useEffect(() => {
    setModalHandler(handleOpenModal, handleUpdateModal, handleCloseModal);
  }, []);

  if (!modalContent?.component) {
    return null;
  }

  return modalContent
    ? ReactDOM.createPortal(
        <modalContent.component {...modalContent.props} closeModal={handleCloseModal} />,
        document.getElementById('modal-root')!,
      )
    : null;
};

export default ModalService;
